export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Semantic tokens. Components never reference raw hex — they read CSS variables
 * produced from these maps, so light/dark stay in sync by construction.
 */
export const tokens = {
  light: {
    'bg': '#eef0f3',
    'surface': '#ffffff',
    'panel': '#f7f8fa',
    'line': '#e5e7ec',
    'line-soft': '#eef0f3',

    'ink': '#14161b',
    'ink-sub': '#5b616e',
    'ink-muted': '#8b909c',
    'ink-faint': '#aeb3bd',

    'accent': '#4f46e5',
    'accent-soft': '#eef0fe',
    'accent-ink': '#3730a3',
    'on-accent': '#ffffff',

    'positive': '#177245',
    'positive-soft': '#e6f3ea',
    'negative': '#c0362c',
    'negative-soft': '#fbecea',
    'warning': '#a5620a',
    'warning-soft': '#fbf1e0',

    'chart-blue': '#2563eb',
    'chart-cyan': '#0891b2',
    'chart-violet': '#7c3aed',
    'chart-pink': '#db2777',
    'chart-amber': '#ca8a04',
    'chart-lime': '#65a30d',
    'chart-red': '#dc2626',
    'chart-slate': '#475569',
    'chart-teal': '#0d9488',

    'overlay': 'rgba(20, 22, 27, 0.40)',
    'shadow': '0 1px 2px rgba(20, 22, 27, 0.04)',
    'shadow-strong': '0 24px 64px rgba(20, 22, 27, 0.16)',
  },
  dark: {
    'bg': '#0f1115',
    'surface': '#171a20',
    'panel': '#1e222a',
    'line': '#2a2f39',
    'line-soft': '#22262e',

    'ink': '#eceef2',
    'ink-sub': '#a4abb8',
    'ink-muted': '#7c8492',
    'ink-faint': '#5d6472',

    'accent': '#7c74f0',
    'accent-soft': '#242244',
    'accent-ink': '#b9b4ff',
    'on-accent': '#ffffff',

    'positive': '#3fbf7f',
    'positive-soft': '#14301f',
    'negative': '#f0736a',
    'negative-soft': '#331a19',
    'warning': '#e0a33f',
    'warning-soft': '#332612',

    'chart-blue': '#5b8ef5',
    'chart-cyan': '#3bb8d4',
    'chart-violet': '#a78bfa',
    'chart-pink': '#f472b6',
    'chart-amber': '#e3b341',
    'chart-lime': '#9bcf5a',
    'chart-red': '#f0736a',
    'chart-slate': '#8b95a8',
    'chart-teal': '#3fc0b0',

    'overlay': 'rgba(0, 0, 0, 0.60)',
    'shadow': '0 1px 2px rgba(0, 0, 0, 0.40)',
    'shadow-strong': '0 24px 64px rgba(0, 0, 0, 0.55)',
  },
} as const;

export type TokenName = keyof typeof tokens.light;

/** Reference a token from inline styles: color: v('ink-sub') */
export const v = (name: TokenName): string => `var(--${name})`;

/** Resolves the project-wide financial-value colour rule (see
 * `utils/format.ts#financialTone`) to an actual token. */
export const financialToneColor = (tone: 'negative' | 'positive' | 'neutral'): string =>
  tone === 'negative' ? v('negative') : tone === 'positive' ? v('positive') : v('ink');

export const applyTheme = (theme: ResolvedTheme): void => {
  const root = document.documentElement;
  const map = tokens[theme];
  Object.entries(map).forEach(([key, value]) => root.style.setProperty(`--${key}`, value));
  root.setAttribute('data-theme', theme);
};

export const font = {
  sans: "'Archivo', system-ui, -apple-system, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};
